/**
 * Página padrão "filtro + abas de elementos" (Brasil Asset III, Spreads de Crédito).
 * O backend devolve `{ abas: [{ titulo, elementos }] }`, e no modo data também `inicio`.
 * No modo período, devolve `periodos` (e `inicio`, com Personalizado). Filtro e aba ativa
 * ficam na URL.
 * Sem data na URL, o backend aplica o padrão da página (ex.: hoje - 365 dias).
 */
import { Alert, Loader, Tabs } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ErroApi, apiGet, type Envelope } from '../api/cliente';
import { useFiltrosUrl } from '../lib/url-state';
import { AvisosApi } from './AvisosApi';
import { Elementos, type ElementoApi } from './Elementos';
import { BarraFiltros, CampoData, CampoOpcoes, CampoPeriodo, GrupoFiltros } from './Filtros';
import { CabecalhoPagina, Pagina, Secao } from './Layout';

export interface OpcaoPeriodo {
  valor: string;
  rotulo: string;
}

/**
 * Data inicial (padrão) ou uma lista de períodos, como o seletor do B3 Fluxo. Com
 * `personalizado`, o seletor ganha a opção de escolher a data inicial (`CampoPeriodo`).
 */
export type FiltroPagina =
  | { tipo: 'data'; grupo?: string }
  | {
      tipo: 'periodo';
      padrao: string;
      periodos: OpcaoPeriodo[];
      grupo?: string;
      personalizado?: boolean;
    };

const FILTRO_DATA: FiltroPagina = { tipo: 'data' };

export interface DadosPaginaComAbas {
  inicio?: string;
  periodo?: string;
  periodos?: OpcaoPeriodo[];
  abas: { titulo: string; elementos: ElementoApi[] }[];
}

export function PaginaComAbas({
  titulo,
  caminho,
  descricao,
  fonte,
  tituloSecao,
  legendaSecao,
  filtro = FILTRO_DATA,
}: {
  titulo: string;
  caminho: string;
  descricao?: string;
  fonte?: string;
  tituloSecao: string;
  legendaSecao?: string;
  filtro?: FiltroPagina;
}) {
  const periodoPadrao = filtro.tipo === 'periodo' ? filtro.padrao : '';
  const [filtros, definir] = useFiltrosUrl({ inicio: '', periodo: periodoPadrao, aba: '' });
  const parametros =
    filtro.tipo === 'data'
      ? { inicio: filtros.inicio }
      : filtro.personalizado
        ? { periodo: filtros.periodo, inicio: filtros.inicio }
        : { periodo: filtros.periodo };
  const consulta = useQuery({
    queryKey: [caminho, parametros],
    queryFn: ({ signal }) => apiGet<Envelope<DadosPaginaComAbas>>(caminho, parametros, signal),
    placeholderData: keepPreviousData,
  });
  const dados = consulta.data?.data;
  const abaAtiva =
    (dados?.abas.some((a) => a.titulo === filtros.aba) && filtros.aba) || dados?.abas[0]?.titulo;

  return (
    <Pagina>
      <CabecalhoPagina
        titulo={titulo}
        descricao={descricao}
        dadosAte={consulta.data?.meta.dados_ate}
        fonte={fonte}
      />
      <BarraFiltros carregando={consulta.isFetching}>
        <GrupoFiltros titulo={filtro.grupo ?? 'Período'}>
          {filtro.tipo === 'periodo' && filtro.personalizado ? (
            <CampoPeriodo
              periodo={filtros.periodo}
              inicio={filtros.inicio}
              inicioEfetivo={dados?.inicio}
              opcoes={dados?.periodos ?? filtro.periodos}
              aoMudar={definir}
            />
          ) : filtro.tipo === 'periodo' ? (
            <CampoOpcoes
              rotulo="Período"
              valor={filtros.periodo}
              aoMudar={(periodo) => definir({ periodo })}
              opcoes={dados?.periodos ?? filtro.periodos}
            />
          ) : (
            <CampoData
              rotulo="Data inicial"
              valor={filtros.inicio || dados?.inicio || ''}
              aoMudar={(inicio) => definir({ inicio })}
            />
          )}
        </GrupoFiltros>
      </BarraFiltros>
      {consulta.isError && (
        <Alert color="red">
          {consulta.error instanceof ErroApi
            ? consulta.error.message
            : 'Não foi possível carregar os dados.'}
        </Alert>
      )}
      {consulta.isPending && <Loader />}
      {dados && (
        <>
          <AvisosApi avisos={[consulta.data?.meta.avisos]} />
          <Secao titulo={tituloSecao} legenda={legendaSecao}>
            <Tabs
              value={abaAtiva}
              onChange={(aba) => aba && definir({ aba })}
              keepMounted={false}
              color="dourado"
            >
              <Tabs.List>
                {dados.abas.map((aba) => (
                  <Tabs.Tab key={aba.titulo} value={aba.titulo}>
                    {aba.titulo}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
              {dados.abas.map((aba) => (
                <Tabs.Panel key={aba.titulo} value={aba.titulo} pt="md">
                  <Elementos elementos={aba.elementos} />
                </Tabs.Panel>
              ))}
            </Tabs>
          </Secao>
        </>
      )}
    </Pagina>
  );
}
